/**
 * Canonical site origin for Supabase Auth redirects
 * (email confirmation, email change, password reset, etc.).
 *
 * Priority:
 * 1. `VITE_SITE_URL` (recommended in prod and for previews / staging)
 * 2. On **production build** without env: project public domain (avoids email
 *    links pointing to `localhost` if the build was made without the variable)
 * 3. Otherwise (dev `vite`): `window.location.origin` → correct localhost locally
 *
 * Important: the **Site URL** in Supabase (Authentication → URL Configuration)
 * must also be the public domain, not `http://localhost:…`, or some template
 * links may stay wrong.
 */
export const PUBLIC_PRODUCTION_ORIGIN = "https://www.kryajet.com";
export const PUBLIC_TEST_ORIGIN = "https://www.sandbox.kryajet.com";
const LOCAL_MAIN_APP_ORIGIN = "http://localhost:8080";

export function isTestAppEnv(): boolean {
  return import.meta.env.VITE_APP_ENV?.trim().toLowerCase() === "test";
}

function resolveDefaultMainAppOrigin(): string {
  if (!import.meta.env.PROD) {
    return LOCAL_MAIN_APP_ORIGIN;
  }
  return isTestAppEnv() ? PUBLIC_TEST_ORIGIN : PUBLIC_PRODUCTION_ORIGIN;
}

/** Main Kryajet web app (www), not the referral portal subdomain. */
export function getMainAppOrigin(): string {
  const fromEnv = import.meta.env.VITE_SITE_ORIGIN as string | undefined;
  if (typeof fromEnv === "string" && fromEnv.trim() !== "") {
    return fromEnv.trim().replace(/\/$/, "");
  }
  const fromSiteUrl = import.meta.env.VITE_SITE_URL as string | undefined;
  if (typeof fromSiteUrl === "string" && fromSiteUrl.trim() !== "") {
    try {
      return new URL(fromSiteUrl.trim()).origin;
    } catch {
      return fromSiteUrl.trim().replace(/\/$/, "");
    }
  }
  return resolveDefaultMainAppOrigin();
}

export function getSiteOrigin(): string {
  const fromEnv = import.meta.env.VITE_SITE_URL as string | undefined;
  if (typeof fromEnv === "string" && fromEnv.trim() !== "") {
    try {
      return new URL(fromEnv.trim()).origin;
    } catch {
      return fromEnv.replace(/\/$/, "");
    }
  }
  // Prod build without VITE_SITE_URL: never embed localhost by mistake in Supabase emails.
  if (import.meta.env.PROD) {
    return resolveDefaultMainAppOrigin();
  }
  if (typeof window !== "undefined") {
    return window.location.origin;
  }
  return "";
}
